import {
  ArrowLeftOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { gql, useMutation } from '@apollo/client';
import {
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  notification,
  Select,
  Space,
  Typography,
} from 'antd';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import CSPage from '../../../components/CSPage';
import CSPageHeader from '../../../components/CSPageHeader';
import {
  createFirmwareVersionAction,
  updateFirmwareVersionAction,
} from '../../../redux-store/firmware-versions-store';
import useIsCreating from '../../../shared/use-is-creating';
import useOne from '../../../shared/use-one';
import {
  createFirmwareVersionMutation,
  DeviceTypes,
  firmwareVersionQuery,
  updateFirmwareVersionMutation,
} from '../constants';
import DeleteFirmwareVersionModal from '../DeleteFirmwareVersionModal';

const { Text } = Typography;

function SelectDeviceType({ value, onChange, ...other }) {
  return (
    <Select
      value={value}
      onChange={onChange}
      placeholder="Select a device type"
      allowClear
      {...other}
    >
      {Object.values(DeviceTypes).map((field) => (
        <Select.Option key={field.key} value={field.key}>
          {field.label}
        </Select.Option>
      ))}
    </Select>
  );
}

function EditFirmwareVersion() {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [updateFirmwareVersion] = useMutation(updateFirmwareVersionMutation);
  const [createFirmwareVersion] = useMutation(createFirmwareVersionMutation);
  const dispatch = useDispatch();

  const { _id } = useParams();
  const queryId = useMemo(() => {
    return Math.round(Math.random() * 10000);
  }, []);

  const fetchResult = useOne(
    firmwareVersionQuery,
    (data) => data && data.firmwareVersion,
    updateFirmwareVersionAction,
    'firmwareVersions',
    _id,
    queryId,
  );

  const [form] = Form.useForm();
  const labelInput = useRef(null);
  const [firmwareVersion, setFirmwareVersion] = useState();

  const onlyOnce = useRef(false);
  useEffect(() => {
    if (!fetchResult.loading && !onlyOnce.current && fetchResult.data) {
      setFirmwareVersion(fetchResult.data);
      onlyOnce.current = true;
    }
  }, [fetchResult, loading]);

  useEffect(() => {
    if (firmwareVersion) {
      const { ...otherFields } = firmwareVersion;
      form.setFieldsValue(otherFields);
    }
  }, [form, firmwareVersion]);

  useLayoutEffect(() => {
    if (labelInput.current) {
      labelInput.current.focus();
    }
  }, []);

  const handleBack = useCallback(() => history.goBack(), [history]);

  const onSubmit = useCallback(
    async (_values) => {
      const { ...values } = _values;
      setLoading(true);
      setError(null);
      try {
        if (firmwareVersion) {
          const result = await updateFirmwareVersion({
            variables: {
              firmwareVersion: {
                _id,
                ...values,
              },
            },
          });
          dispatch(
            updateFirmwareVersionAction(
              result.data.updateFirmwareVersion.firmwareVersion,
            ),
          );
          notification.success({
            message: 'Saved',
            description: 'Firmware Version updated successfully',
          });
        } else {
          const result = await createFirmwareVersion({
            variables: {
              firmwareVersion: {
                _id,
                ...values,
              },
            },
          });
          dispatch(
            createFirmwareVersionAction(
              result.data.createFirmwareVersion.firmwareVersion,
            ),
          );
          notification.success({
            message: 'Saved',
            description: 'Firmware Version created successfully',
          });
        }
        history.push('/fw-versions');
      } catch (err) {
        console.error(err);
        setError(err.message);
      }
      setLoading(false);
    },
    [
      firmwareVersion,
      _id,
      history,
      createFirmwareVersion,
      updateFirmwareVersion,
      dispatch,
    ],
  );

  const { title, showDelete } = useIsCreating(
    location,
    firmwareVersion,
    loading,
    'Create New Firmware Version',
    'Edit Firmware Version',
  );

  return (
    <>
      <CSPage>
        <CSPageHeader
          title={title}
          backActions={[
            <Link key="back" to="/fw-versions">
              <Button type="text" icon={<ArrowLeftOutlined />}>
                Back to Firmware Versions
              </Button>
            </Link>,
          ]}
        />
        <Form
          layout="vertical"
          onFinish={onSubmit}
          id="editFirmwareVersion"
          form={form}
          style={{ maxWidth: 500 }}
        >
          <Form.Item
            label="Device Type"
            name="deviceType"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <SelectDeviceType ref={labelInput} disabled={loading} />
          </Form.Item>
          <Form.Item
            label="App Name"
            name="appName"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input disabled={loading} />
          </Form.Item>
          <Form.Item
            label="App Flavor"
            name="appFlavor"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input disabled={loading} />
          </Form.Item>
          <Form.Item
            label="Version Name"
            name="versionName"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input disabled={loading} />
          </Form.Item>
          <Form.Item
            label="Version Number"
            name="versionNumber"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <InputNumber min={0} step={1} disabled={loading} />
          </Form.Item>
          <Form.Item label="Tags" name="tags">
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Type to create a tag"
            />
          </Form.Item>
          <Form.Item label="Notes" name="notes">
            <Input.TextArea disabled={loading} rows={4} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldsError }) => {
              const errorList = getFieldsError();
              let showError = false;
              errorList.forEach((errors) => {
                if (errors.errors.length) {
                  showError = true;
                }
              });
              return (
                showError && (
                  <Text type="danger" style={{ marginTop: 16 }}>
                    Please correct the errors above
                  </Text>
                )
              );
            }}
          </Form.Item>
          <div style={{ height: 16 }} />
          <Form.Item>
            <Space
              style={{
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <Button
                key="cancel"
                onClick={handleBack}
                htmlType="button"
                type="text"
                size="small"
                disabled={loading}
                icon={<ArrowLeftOutlined />}
                style={{ marginLeft: -7 }}
              >
                Cancel
              </Button>
              <Button
                key="send"
                type="primary"
                loading={loading}
                htmlType="submit"
              >
                Save Firmware Version
              </Button>
            </Space>
          </Form.Item>
          {error && (
            <div style={{ marginTop: 16 }}>
              <Text type="danger">{error}</Text>
            </div>
          )}
          {showDelete && (
            <>
              <Divider />
              <div className="delete-box">
                <Space align="center">
                  <Button
                    onClick={() => setDeleting(firmwareVersion)}
                    icon={<DeleteOutlined />}
                    danger
                    ghost
                  >
                    Delete Firmware Version
                  </Button>
                </Space>
              </div>
            </>
          )}
        </Form>
        <div style={{ minHeight: 300 }} />
        <DeleteFirmwareVersionModal
          setFirmwareVersion={setDeleting}
          firmwareVersion={deleting}
        />
      </CSPage>
      <style jsx>{`
        .delete-box {
          display: flex;
          justify-content: center;
        }
      `}</style>
    </>
  );
}

export default EditFirmwareVersion;
